import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, X } from 'lucide-react';
import { adsApi } from '@/api/client';
import {
  advertisedProductKey,
  applyEligibilityStatuses,
  filterAdvertisedProducts,
  formatEnteredProductLookupMessage,
  isAdvertisedProductAlreadyAdded,
  isAdvertisedProductIneligible,
  parseAsinList,
  parseProductEligibilityMap,
  parseProductMetadataPayload,
  parseSearchQueryAsProductIds,
  productsForSearchDropdown,
  resolveEnteredAdvertisedProducts,
  splitEnteredProductIds,
} from '@/utils/spCampaignCreateDraft';
import type { SpAdvertisedProduct } from '@/types/spCampaignCreate';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/Popover';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';

type PickerTab = 'search' | 'list';

async function fetchAdvertisedProducts(
  profileId: string,
  body: Record<string, unknown>
): Promise<SpAdvertisedProduct[]> {
  const res = await adsApi.getProductMetadata(profileId, {
    adType: 'SP',
    pageSize: 100,
    pageIndex: 0,
    checkItemDetails: true,
    checkEligibility: true,
    ...body,
  });
  if (!res.success) {
    throw new Error(res.message || 'Could not load products.');
  }
  let products = parseProductMetadataPayload(res.data);
  const needsElig = products.filter((product) => !product.eligibilityStatus && product.asin);
  if (needsElig.length === 0) return products;
  try {
    const eligRes = await adsApi.getProductEligibility(profileId, {
      adType: 'sp',
      productDetailsList: needsElig.map((product) => ({
        asin: product.asin,
        ...(product.sku ? { sku: product.sku } : {}),
      })),
    });
    if (eligRes.success) {
      products = applyEligibilityStatuses(products, parseProductEligibilityMap(eligRes.data));
    }
  } catch {
    // Eligibility is best-effort; metadata results still display.
  }
  return products;
}

async function fetchAdvertisedProductsByIds(
  profileId: string,
  asins: string[],
  skus: string[]
): Promise<SpAdvertisedProduct[]> {
  const requests: Promise<SpAdvertisedProduct[]>[] = [];
  if (asins.length) {
    requests.push(
      fetchAdvertisedProducts(profileId, {
        asins,
        pageSize: Math.min(100, Math.max(25, asins.length)),
        pageIndex: 0,
      })
    );
  }
  if (skus.length) {
    requests.push(
      fetchAdvertisedProducts(profileId, {
        skus,
        pageSize: Math.min(100, Math.max(25, skus.length)),
        pageIndex: 0,
      })
    );
  }
  if (requests.length === 0) return [];
  const pages = await Promise.allSettled(requests);
  const out: SpAdvertisedProduct[] = [];
  const seen = new Set<string>();
  let firstError: unknown = null;
  for (const page of pages) {
    if (page.status === 'fulfilled') {
      for (const product of page.value) {
        const key = advertisedProductKey(product);
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(product);
      }
    } else if (firstError == null) {
      firstError = page.reason;
    }
  }
  if (out.length === 0 && firstError) throw firstError;
  return out;
}

function ProductOptionRow({
  product,
  added,
  disabled,
  onAdd,
}: {
  product: SpAdvertisedProduct;
  added: boolean;
  disabled?: boolean;
  onAdd: () => void;
}) {
  const ineligible = isAdvertisedProductIneligible(product);
  return (
    <li className="flex items-center gap-3 p-2">
      {product.imageUrl ? (
        <img src={product.imageUrl} alt="" className="w-10 h-10 rounded object-cover" />
      ) : (
        <div className="w-10 h-10 rounded bg-surface-muted" />
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm truncate">{product.title || product.asin || product.sku}</p>
        <p className="text-xs text-foreground-muted">
          {product.asin ? `ASIN: ${product.asin}` : ''}
          {product.asin && product.sku ? ' · ' : ''}
          {product.sku ? `SKU: ${product.sku}` : ''}
          {ineligible ? ' · Ineligible' : ''}
        </p>
      </div>
      <button
        type="button"
        className="text-sm text-brand disabled:opacity-40"
        disabled={disabled || added || ineligible}
        onClick={onAdd}
      >
        {added ? 'Added' : ineligible ? 'Ineligible' : 'Add'}
      </button>
    </li>
  );
}

export function AdvertisedProductsPicker({
  profileId,
  products,
  onChange,
  disabled,
}: {
  profileId: string;
  products: SpAdvertisedProduct[];
  onChange: (products: SpAdvertisedProduct[]) => void;
  disabled?: boolean;
}) {
  const [tab, setTab] = useState<PickerTab>('search');
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [listText, setListText] = useState('');
  const [listIneligible, setListIneligible] = useState<SpAdvertisedProduct[]>([]);
  const [listLookedUp, setListLookedUp] = useState(false);
  const [listMessage, setListMessage] = useState<string | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query), 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  const catalogQuery = useQuery({
    queryKey: ['product-metadata-catalog', profileId],
    queryFn: () => fetchAdvertisedProducts(profileId, { pageSize: 100, pageIndex: 0 }),
    enabled: Boolean(profileId),
    staleTime: 60_000,
    retry: false,
  });

  const catalog = catalogQuery.data ?? [];
  const catalogSettled = catalogQuery.isSuccess || catalogQuery.isError;
  const localMatches = useMemo(
    () => filterAdvertisedProducts(catalog, query),
    [catalog, query]
  );
  const localMatchesForDebounced = useMemo(
    () => filterAdvertisedProducts(catalog, debouncedQuery),
    [catalog, debouncedQuery]
  );
  const trimmedDebounced = debouncedQuery.trim();
  const searchIds = useMemo(
    () => parseSearchQueryAsProductIds(trimmedDebounced),
    [trimmedDebounced]
  );
  const catalogMiss =
    catalogSettled && (catalogQuery.isError || localMatchesForDebounced.length === 0);
  const needsIdLookup = Boolean(searchIds) && catalogMiss;
  const needsTitleSearch = Boolean(trimmedDebounced) && !searchIds && catalogMiss;

  const idLookupQuery = useQuery({
    queryKey: ['product-metadata-ids', profileId, searchIds],
    queryFn: () => {
      const { asins, skus } = splitEnteredProductIds(searchIds ?? []);
      return fetchAdvertisedProductsByIds(profileId, asins, skus);
    },
    enabled: Boolean(profileId) && needsIdLookup,
    staleTime: 30_000,
    retry: false,
  });

  const searchQuery = useQuery({
    queryKey: ['product-metadata-search', profileId, trimmedDebounced],
    queryFn: () =>
      fetchAdvertisedProducts(profileId, { searchStr: trimmedDebounced, pageSize: 25 }),
    enabled: Boolean(profileId) && needsTitleSearch,
    staleTime: 30_000,
    retry: false,
  });

  const dropdownProducts = useMemo(
    () =>
      productsForSearchDropdown({
        query,
        catalog,
        localMatches,
        idLookup: idLookupQuery.data ?? [],
        titleSearch: searchQuery.data ?? [],
      }),
    [query, catalog, localMatches, idLookupQuery.data, searchQuery.data]
  );

  const addResolvedIds = (ids: string[], metadata: SpAdvertisedProduct[]) => {
    const result = resolveEnteredAdvertisedProducts({
      ids,
      metadata,
      catalog,
      existing: products,
    });
    if (result.toAdd.length > 0) {
      onChange([...products, ...result.toAdd]);
    }
    return result;
  };

  const lookupList = async () => {
    const ids = parseAsinList(listText);
    if (ids.length === 0) return;
    const { asins, skus } = splitEnteredProductIds(ids);
    setListLoading(true);
    setListError(null);
    setListMessage(null);
    try {
      let metadata: SpAdvertisedProduct[] = [];
      try {
        metadata = await fetchAdvertisedProductsByIds(profileId, asins, skus);
      } catch (err) {
        setListError(err instanceof Error ? err.message : 'Could not load products.');
      }
      const result = addResolvedIds(ids, metadata);
      setListIneligible(result.skippedIneligible);
      setListMessage(formatEnteredProductLookupMessage(result));
      setListLookedUp(true);
      setListText('');
    } finally {
      setListLoading(false);
    }
  };

  const addFromSearchQuery = () => {
    const ids = parseSearchQueryAsProductIds(query);
    if (!ids) return false;
    addResolvedIds(ids, idLookupQuery.data ?? []);
    setQuery('');
    setDropdownOpen(false);
    return true;
  };

  const addProduct = (product: SpAdvertisedProduct) => {
    if (isAdvertisedProductAlreadyAdded(products, product) || isAdvertisedProductIneligible(product)) {
      return;
    }
    onChange([...products, product]);
  };

  const removeProduct = (product: SpAdvertisedProduct) => {
    const key = advertisedProductKey(product);
    onChange(products.filter((item) => advertisedProductKey(item) !== key));
  };

  const dropdownLoading =
    catalogQuery.isLoading ||
    (Boolean(query.trim()) &&
      localMatches.length === 0 &&
      (idLookupQuery.isFetching || searchQuery.isFetching));
  const dropdownError =
    query.trim() &&
    localMatches.length === 0 &&
    !parseSearchQueryAsProductIds(query) &&
    searchQuery.isError
      ? searchQuery.error instanceof Error
        ? searchQuery.error.message
        : 'Could not load products.'
      : null;

  let dropdownEmpty: string | null = null;
  if (!dropdownLoading && dropdownProducts.length === 0 && !dropdownError) {
    if (!query.trim() && catalogQuery.isError) {
      dropdownEmpty = "Type to search this account's products.";
    } else if (!query.trim()) {
      dropdownEmpty = 'No products found for this account.';
    } else {
      dropdownEmpty = 'No matching products.';
    }
  }

  return (
    <fieldset className="border border-border rounded-xl p-4 space-y-3">
      <legend className="text-sm font-medium text-foreground-muted px-1">Products</legend>
      <p className="text-xs text-foreground-muted">
        Select from ASINs in this account. Or enter ASINs / SKUs.
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-3">
          <div className="flex gap-2 text-sm">
            {(['search', 'list'] as const).map((id) => (
              <button
                key={id}
                type="button"
                className={`px-3 py-1 rounded-lg border ${
                  tab === id ? 'border-brand text-brand bg-brand/5' : 'border-border'
                }`}
                onClick={() => setTab(id)}
                disabled={disabled}
              >
                {id === 'search' ? 'Search' : 'Enter list'}
              </button>
            ))}
          </div>
          {tab === 'search' ? (
            <Popover open={dropdownOpen} onOpenChange={setDropdownOpen} modal={false}>
              <PopoverAnchor asChild>
                <div className="relative">
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      setDropdownOpen(true);
                    }}
                    onFocus={() => setDropdownOpen(true)}
                    onKeyDown={(e) => {
                      if (e.key !== 'Enter' || disabled) return;
                      if (addFromSearchQuery()) e.preventDefault();
                    }}
                    placeholder="Search by title, ASIN, or SKU"
                    className="w-full border border-border rounded-lg px-3 py-2 pr-9 text-sm"
                    disabled={disabled}
                    aria-haspopup="listbox"
                    aria-expanded={dropdownOpen}
                    aria-label="Select ASINs from this account"
                    autoComplete="off"
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-foreground-muted hover:text-foreground"
                    disabled={disabled}
                    aria-label="Toggle account ASINs"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={() => setDropdownOpen((open) => !open)}
                  >
                    <ChevronDown className="h-4 w-4" aria-hidden />
                  </button>
                </div>
              </PopoverAnchor>
              <PopoverContent
                align="start"
                className={cn(
                  'w-[var(--radix-popper-anchor-width)] min-w-[16rem] max-w-[calc(100vw-2rem)] p-0'
                )}
                onOpenAutoFocus={(e) => e.preventDefault()}
              >
                {dropdownLoading ? (
                  <div className="flex items-center gap-2 px-3 py-3 text-sm text-foreground-muted">
                    <Spinner className="w-4 h-4" />
                    Loading products…
                  </div>
                ) : dropdownError ? (
                  <p className="px-3 py-3 text-xs text-error">{dropdownError}</p>
                ) : dropdownEmpty ? (
                  <p className="px-3 py-3 text-sm text-foreground-subtle">{dropdownEmpty}</p>
                ) : (
                  <ul
                    className="divide-y divide-border max-h-64 overflow-auto"
                    role="listbox"
                    aria-label="Account ASINs"
                  >
                    {dropdownProducts.map((product) => (
                      <ProductOptionRow
                        key={advertisedProductKey(product)}
                        product={product}
                        added={isAdvertisedProductAlreadyAdded(products, product)}
                        disabled={disabled}
                        onAdd={() => addProduct(product)}
                      />
                    ))}
                  </ul>
                )}
              </PopoverContent>
            </Popover>
          ) : (
            <div className="space-y-2">
              <textarea
                value={listText}
                onChange={(e) => setListText(e.target.value)}
                placeholder="Enter ASINs or SKUs, one per line"
                className="w-full min-h-[88px] border border-border rounded-lg px-3 py-2 text-sm"
                disabled={disabled}
              />
              <button
                type="button"
                className="text-sm text-brand"
                disabled={disabled || listLoading || !listText.trim()}
                onClick={() => void lookupList()}
              >
                Look up products
              </button>
            </div>
          )}
          {tab === 'list' && listError && <p className="text-xs text-error">{listError}</p>}
          {tab === 'list' && listMessage && (
            <p className="text-sm text-foreground-muted">{listMessage}</p>
          )}
          {tab === 'list' &&
            (listLoading ? (
              <div className="flex items-center gap-2 text-sm text-foreground-muted">
                <Spinner className="w-4 h-4" />
                Loading products…
              </div>
            ) : listIneligible.length > 0 ? (
              <ul className="divide-y divide-border border border-border rounded-lg max-h-64 overflow-auto">
                {listIneligible.map((product) => (
                  <ProductOptionRow
                    key={advertisedProductKey(product)}
                    product={product}
                    added={false}
                    disabled
                    onAdd={() => undefined}
                  />
                ))}
              </ul>
            ) : !listLookedUp ? (
              <p className="text-sm text-foreground-subtle">
                Enter ASINs or SKUs, then look them up to add.
              </p>
            ) : null)}
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium">{products.length} added</p>
            {products.length > 0 && (
              <button
                type="button"
                className="text-sm text-brand"
                disabled={disabled}
                onClick={() => onChange([])}
              >
                Remove all
              </button>
            )}
          </div>
          {products.length === 0 ? (
            <p className="text-sm text-foreground-subtle border border-dashed border-border rounded-lg p-4">
              Add at least one product to advertise.
            </p>
          ) : (
            <ul className="divide-y divide-border border border-border rounded-lg">
              {products.map((product) => (
                <li key={advertisedProductKey(product)} className="flex items-center gap-3 p-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm truncate">{product.title || product.asin || product.sku}</p>
                    <p className="text-xs text-foreground-muted">
                      {product.asin || product.sku}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="text-foreground-muted hover:text-foreground"
                    disabled={disabled}
                    onClick={() => removeProduct(product)}
                    aria-label="Remove product"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </fieldset>
  );
}
